import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { supabase } from '@/integrations/supabase/client';
import { useGameStore } from '@/lib/gameStore';
import { Button } from './ui/button';
import PlayerAvatar from './PlayerAvatar';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface GameEndProps {
  onPlayAgain: () => void;
  onBackToMenu: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function GameEnd({ onPlayAgain, onBackToMenu, setMusicIntensity }: GameEndProps) {
  const { currentRoom } = useGameStore();
  const [gameHistory, setGameHistory] = useState<any[]>([]);
  const [mvp, setMvp] = useState<any>(null);
  const [chaosAgent, setChaosAgent] = useState<any>(null);

  const isWin = currentRoom?.currentTotal === currentRoom?.targetTotal;

  // Set victory or defeat music intensity
  useEffect(() => {
    setMusicIntensity(isWin ? 'victory' : 'defeat');
  }, [isWin, setMusicIntensity]);

  useEffect(() => {
    if (!currentRoom) return;

    // Load game history
    const loadHistory = async () => {
      const { data } = await supabase
        .from('game_history')
        .select('*')
        .eq('room_id', currentRoom.id)
        .order('round_number', { ascending: true });

      if (data) {
        setGameHistory(data);
        calculateMVP(data);
      }
    };

    loadHistory();

    // Trigger confetti for wins
    if (isWin) {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b']
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
    }
  }, [currentRoom?.id, isWin]);

  const calculateMVP = (history: any[]) => {
    if (!currentRoom) return;

    const playerContributions = new Map();

    history.forEach((round) => {
      const selections = round.selections || [];
      selections.forEach((selection: any) => {
        if (!playerContributions.has(selection.player_id)) {
          playerContributions.set(selection.player_id, { good: 0, bad: 0 });
        }

        const contrib = playerContributions.get(selection.player_id);
        if (round.moved_closer) {
          contrib.good++;
        } else {
          contrib.bad++;
        }
      });
    });

    // Find MVP (most good contributions)
    let mvpId = null;
    let maxGood = -1;

    // Find Chaos Agent (most bad contributions)
    let chaosId = null;
    let maxBad = -1;

    playerContributions.forEach((contrib, playerId) => {
      if (contrib.good > maxGood) {
        maxGood = contrib.good;
        mvpId = playerId;
      }
      if (contrib.bad > maxBad) {
        maxBad = contrib.bad;
        chaosId = playerId;
      }
    });

    const mvpPlayer = currentRoom.players.find(p => p.id === mvpId);
    const chaosPlayer = currentRoom.players.find(p => p.id === chaosId);

    setMvp(mvpPlayer);
    setChaosAgent(chaosPlayer);
  };

  const handlePlayAgain = async () => {
    if (!currentRoom) return;

    // Reset room
    await supabase
      .from('rooms')
      .update({
        status: 'lobby',
        current_round: 0,
        current_total: 0,
        target_total: 67
      })
      .eq('id', currentRoom.id);

    // Clear selections
    await supabase
      .from('round_selections')
      .delete()
      .eq('room_id', currentRoom.id);

    onPlayAgain();
  };

  if (!currentRoom) return null;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <motion.div
        className="max-w-4xl mx-auto space-y-8"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <motion.div
            className="text-9xl font-black text-shadow"
            animate={{ 
              scale: [1, 1.05, 1],
              rotate: isWin ? [0, 5, -5, 0] : 0
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {isWin ? '🎉' : '💀'}
          </motion.div>
          
          <h1 className={`text-6xl md:text-8xl font-black ${
            isWin ? 'text-chaos-green' : 'text-chaos-red'
          } text-shadow`}>
            {isWin ? 'YOU DID IT!' : 'GAME OVER'}
          </h1>

          <p className="text-2xl font-bold text-muted-foreground">
            {isWin ? 'Chaos conquered! 🔥' : 'The chaos was too much... 😔'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">FINAL TOTAL</div>
            <div className="text-4xl font-black text-primary">{currentRoom.currentTotal}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">TARGET</div>
            <div className="text-4xl font-black text-chaos-green">{currentRoom.targetTotal}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">ROUNDS</div>
            <div className="text-4xl font-black text-foreground">{currentRoom.currentRound}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">PLAYERS</div>
            <div className="text-4xl font-black text-foreground">{currentRoom.players.length}</div>
          </div>
        </div>

        {/* MVP & Chaos Agent */}
        <div className="grid md:grid-cols-2 gap-6">
          {mvp && (
            <motion.div
              className="glass-card p-6 rounded-3xl border-2 border-chaos-green/50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-center space-y-4">
                <div className="text-4xl">🏆</div>
                <div className="text-2xl font-black text-chaos-green">MVP</div>
                <PlayerAvatar player={mvp} size="lg" />
                <div className="text-xl font-bold text-foreground">{mvp.name}</div>
                <div className="text-sm text-muted-foreground">Made the clutch plays! 🔥</div>
              </div>
            </motion.div>
          )}

          {chaosAgent && (
            <motion.div
              className="glass-card p-6 rounded-3xl border-2 border-chaos-red/50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-center space-y-4">
                <div className="text-4xl">💀</div>
                <div className="text-2xl font-black text-chaos-red">CHAOS AGENT</div>
                <PlayerAvatar player={chaosAgent} size="lg" />
                <div className="text-xl font-bold text-foreground">{chaosAgent.name}</div>
                <div className="text-sm text-muted-foreground">Kept things spicy! 🌶️</div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Button
            onClick={handlePlayAgain}
            size="lg"
            className="text-2xl px-12 py-6 h-auto font-black rounded-2xl"
          >
            🔄 PLAY AGAIN
          </Button>
          
          <Button
            onClick={onBackToMenu}
            size="lg"
            variant="outline"
            className="text-2xl px-12 py-6 h-auto font-black rounded-2xl"
          >
            🏠 BACK TO MENU
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
